import React, { FC } from "react";
import { useTheme, withThemeProvider } from "@narvar/nth-hook-theme";
import Icon from "../Icon";
import openingConversion from "../../transformers/timeFormatTranslation";
import { useStyles } from "./LocationCardStyles";
import Language from "../Language";
import { LocationCardProps } from "./types";
import TextLink from "../TextLink";
import SelectItem from "../SelectItem/SelectItem";
import { Location } from "@narvar/nth-kit-returns-headless";

const DistanceItemC = ({ location }: { location: Location }) => {
  const { linksNoneDefault, linksNoneHover } = useTheme();

  const styles = useStyles();
  const hasExactLocation =
    location.latitude && location.longitude && location.distance;

  if (!hasExactLocation && !location.address) return null;

  return (
    <div className={styles.locationItemDistance}>
      <TextLink
        href={`https://maps.google.com/?q=${encodeURIComponent(
          [
            location.displayName,
            location?.address?.address1,
            location?.address?.address2,
            location?.address?.city,
            location?.address?.regionCode,
            location?.address?.countryCode,
            location?.address?.postalCode,
          ]
            .filter(Boolean)
            .join(" "),
        )}`}
        target="_blank"
        linkStyle={{
          sDefault: linksNoneDefault,
          sHover: linksNoneHover,
        }}>
        {hasExactLocation && `${location?.distance?.value?.toFixed(1) ?? ""} `}
        <Language
          langKey={
            location.distance?.unit
              ? `returnsPudoLocationsDistanceUnit${location.distance?.unit.toLowerCase()}`
              : "returnsPudoLocationsDistanceNounit"
          }
        />
      </TextLink>
    </div>
  );
};

const DistanceItem = withThemeProvider(DistanceItemC, {
  themeTokens: ["linksNoneDefault", "linksNoneHover"],
});

const AddressItem = ({ location: { address } }: { location: Location }) => {
  const styles = useStyles();
  if (!address) {
    return null;
  }
  return (
    <address className={styles.locationItemAddress}>
      {[address.address1, address.address2, address.city, address.regionCode]
        .filter(Boolean)
        .join(", ")}
      {` ${address.postalCode}`}
    </address>
  );
};

const LocationCardC: FC<LocationCardProps> = ({
  location,
  active,
  onValueChange,
  transparent,
}) => {
  const { bodytextDefaultNone, headersSmallNone, panelcontainerNoneNone } =
    useTheme();

  const styles = useStyles();

  const openingHoursDisplay =
    location.openingHoursDisplayString ||
    (location.openingHours ? openingConversion(location.openingHours) : "");
  return (
    <SelectItem
      customStyle={{
        display: "grid",
        width: "100%",
        gridAutoFlow: "row",
        gridTemplateColumns: "auto 1fr auto",
        alignItems: "center",
        justifyContent: "start",
        gridColumnGap: "32px",
        gridRowGap: "5px",
        gridTemplateAreas: active
          ? `
            'Icon Title Distance'
            'Icon Subtitle Distance'
            'Icon Address .'
            'Icon Details Details'
          `
          : `'Icon Title Distance'`,
        fontSize: bodytextDefaultNone?.fontSize ?? "14px",
        ...panelcontainerNoneNone,
      }}
      value={location.id}
      key={location.id}
      onClick={() => onValueChange?.(location.id)}
      selected={active}
      transparent={transparent}
      readOnly={transparent}
      borderless={transparent}>
      {location.logoUrls?.length && (
        <div className={styles.locationItemIcon}>
          <img
            alt={location.displayName || location?.storeName || location.id}
            src={location.logoUrls[0]}
            style={{ width: "100%" }}
          />
        </div>
      )}
      <div
        className={styles.locationItemTitle}
        style={{ fontSize: headersSmallNone?.fontSize ?? "14px" }}>
        {location.displayName || location.storeName}
      </div>
      {active && location.locationType && (
        <div className={styles.locationItemSubtitle}>
          {location.locationType}
        </div>
      )}
      {active && <AddressItem location={location} />}
      {active && openingHoursDisplay && (
        <div className={styles.locationItemHours}>
          <Icon icon="Clock" style={{ alignSelf: "center" }} />
          {openingHoursDisplay}
        </div>
      )}
      <DistanceItem location={location} />
    </SelectItem>
  );
};

export default withThemeProvider(LocationCardC, {
  themeTokens: [
    "documentbodyNoneNone",
    "bodytextDefaultNone",
    "headersSmallNone",
    "linksNoneDefault",
    "linksNoneHover",
    "panelcontainerNoneNone",
    "selecteditemNoneNone",
  ],
});
